import { useState } from 'react';

import { InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type {
  AwardAmountField,
  AwardAmounts,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { AmountField } from './AmountField';

const STRINGS = appStrings.modules.myColleges.awardLetter;

type AmountsModalProps = Readonly<{
  field: AwardAmountField;
  amounts: AwardAmounts;
  saving: boolean;
  saveFailed: boolean;
  onClose: () => void;
  onSave: (amounts: AwardAmounts) => void;
}>;

export const AmountsModal = ({
  field,
  amounts,
  saving,
  saveFailed,
  onClose,
  onSave,
}: AmountsModalProps) => {
  const [draft, setDraft] = useState(amounts[field]);
  const { label, tooltip } = STRINGS.fields[field];

  return (
    <InfoModal
      open
      onClose={onClose}
      width="sm"
      featuredIcon={{ iconName: 'Edit02', size: 'lg', color: 'Colors/Foreground/fg-secondary' }}
      headerContent={
        <Box>
          <Typography variant="text-md-semibold" component="h2">
            {STRINGS.edit.title(label)}
          </Typography>
          <Typography variant="text-sm-regular" sx={mutedSx}>
            {STRINGS.edit.subtitle}
          </Typography>
        </Box>
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box
          component="form"
          sx={bodySx}
          onSubmit={(event) => {
            event.preventDefault();
            onSave({ ...amounts, [field]: draft });
          }}
        >
          <AmountField
            id={`award-letter-${field.replace(/_/g, '-')}-input`}
            label={label}
            tooltip={tooltip}
            value={draft}
            onChange={setDraft}
          />
          {saveFailed && (
            <Typography id="award-letter-edit-failed" variant="text-sm-medium" sx={errorSx}>
              {STRINGS.saveFailed}
            </Typography>
          )}
          <Button
            id="award-letter-edit-save"
            type="submit"
            hierarchy="Primary"
            size="md"
            fullWidth
            disabled={saving}
          >
            {STRINGS.edit.save}
          </Button>
        </Box>
      }
    />
  );
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/600'],
});

const errorSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-error-primary'],
});
